import { zodResolver } from '@hookform/resolvers/zod'
import { useAtomValue } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import React from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { ExecuteCancelDialog } from '~/components/elements/ExecuteCancelDialog'
import { LabelingContainer } from '~/components/elements/LabelingContainer'
import { StyledInput } from '~/components/elements/StyledInput'
import { useFormField } from '~/hooks/useFormField'
import { useLogEvent } from '~/hooks/useLogEvent'
import { useNotify } from '~/hooks/useNotify'
import { useNotifyOnError } from '~/hooks/useNotifyOnError'
import { exists } from '~/utils'

import { dialogInfoAtom } from '../atoms'
import { useAddBrandAccountAllowDomainMutation } from '../hooks/useAddBrandAccountAllowDomainMutation'
import { useBrandAccountId } from '../hooks/useBrandAccountId'
import { useCloseDialogWithRefresh } from '../hooks/useCloseDialogWithRefresh'

const schema = z.object({
  domainName: z.string().trim().min(1),
})

type InputValues = z.infer<typeof schema>
const resolver = zodResolver(schema)

export const AddAllowedDomainDialog = () => {
  const dialogInfo = useAtomValue(dialogInfoAtom)
  if (dialogInfo?.dialogType !== 'add') {
    throw new Error()
  }

  const { t } = useTranslation()

  const resetDialogInfo = useResetAtom(dialogInfoAtom)
  const brandAccountId = useBrandAccountId()
  const notifyError = useNotifyOnError()
  const closeDialogWithRefresh = useCloseDialogWithRefresh()
  const logEvent = useLogEvent()
  const addBrandAccountAllowDomainMutation =
    useAddBrandAccountAllowDomainMutation({
      onSuccess: (data) => {
        notify({
          style: 'success',
          content: t('allowed_domain_added', {
            domain: data.addBrandAccountAllowDomain?.allowDomain,
          }),
        })
        // ユーザー不在などaddBrandAccountAllowDomainはallowDomainを返さない。追加できていないので記録しない。
        if (exists(data.addBrandAccountAllowDomain?.allowDomain)) {
          logEvent('Add allowed domain')
        }
        closeDialogWithRefresh()
      },
      onError: (data) => {
        console.error(data)
        notifyError(data, {
          403: t('no_permission_to_add_allowed_domains'),
          409: t('this_domain_is_already_registered'),
        })
        resetDialogInfo()
      },
    })
  const notify = useNotify()

  const {
    control,
    formState: { isDirty, isValid },
    handleSubmit,
  } = useForm<InputValues>({
    defaultValues: { domainName: '' },
    resolver,
  })

  const domainNameField = useFormField(control, 'domainName')

  const handleValid = React.useCallback<SubmitHandler<InputValues>>(
    async ({ domainName }) => {
      if (!isDirty) {
        resetDialogInfo()

        return
      }

      addBrandAccountAllowDomainMutation.mutate({
        allowDomain: domainName ?? '',
        brandAccountId,
      })

      resetDialogInfo()
    },
    [
      isDirty,
      resetDialogInfo,
      addBrandAccountAllowDomainMutation,
      brandAccountId,
    ],
  )

  const handleCancel = React.useCallback(() => {
    resetDialogInfo()
  }, [resetDialogInfo])

  return (
    <ExecuteCancelDialog
      disabledExecute={!isValid}
      executing={false}
      mode="create"
      title={t('add_allowed_domain')}
      onCancel={handleCancel}
      onExecute={handleSubmit(handleValid)}
    >
      <LabelingContainer label={t('domain')}>
        <StyledInput
          autoFocus={true}
          placeholder="example.com"
          width="100%"
          {...domainNameField}
        />
      </LabelingContainer>
    </ExecuteCancelDialog>
  )
}
